<script setup lang="ts">
import { computed, ref } from 'vue'
import { ArrowUpIcon, ArrowsUpDownIcon, ArrowDownIcon } from '@heroicons/vue/24/solid'
import type { SortDirection } from '@/types/tableTypes'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { MIN_COLUMN_WIDTH_PX } from '@/components/DataTable/minColumnWidth'

const {
  columnId,
  name,
  width,
  sortDirection,
  isSortable,
  isPinned,
  resizable = true,
} = defineProps<{
  columnId: string
  name: string
  width?: number
  sortDirection?: SortDirection
  isPinned?: boolean
  isVisible?: boolean
  isSortable?: boolean
  /** When false, the drag-resize / double-click-autosize handle is not rendered. */
  resizable?: boolean
}>()

const emit = defineEmits<{
  'sort': []
  'resize': [width: number]
  'reorder': [draggedColumnId: string, targetColumnId: string]
  'autoSize': []
}>()

const isResizing = ref(false)
const startX = ref(0)
const startWidth = ref(0)
const isDragging = ref(false)
const draggedColumnId = ref<string>('')

const handleSort = () => {
  if (!isSortable) return
  emit('sort')
}

const handleResizeEnd = () => {
  isResizing.value = false
  document.removeEventListener('mousemove', handleResizeMove)
  document.removeEventListener('mouseup', handleResizeEnd)
}

const handleResizeMove = (e: MouseEvent) => {
  if (!isResizing.value) return

  const deltaX = e.clientX - startX.value
  const newWidth = Math.max(MIN_COLUMN_WIDTH_PX, startWidth.value + deltaX)
  emit('resize', newWidth)
}

const handleResizeStart = (event: MouseEvent) => {
  isResizing.value = true
  startX.value = event.clientX
  startWidth.value = width || 150

  document.addEventListener('mousemove', handleResizeMove)
  document.addEventListener('mouseup', handleResizeEnd)
}

const handleResizeHandleDblClick = (e: MouseEvent) => {
  e.preventDefault()
  e.stopPropagation()
  if (isResizing.value) {
    handleResizeEnd()
  }
  emit('autoSize')
}

const handleDragStart = (e: DragEvent) => {
  if (isResizing.value) {
    e.preventDefault()
    return
  }
  if (!e.dataTransfer) return

  isDragging.value = true
  draggedColumnId.value = columnId
  e.dataTransfer.effectAllowed = 'move'
  e.dataTransfer.setData('text/plain', columnId)

  const headerElement = e.currentTarget as HTMLElement
  if (headerElement) {
    e.dataTransfer.setDragImage(headerElement, headerElement.offsetWidth / 2, headerElement.offsetHeight / 2)
  }
}

const handleDragEnd = () => {
  isDragging.value = false
  draggedColumnId.value = ''
}

const handleDragOver = (e: DragEvent) => {
  if (!e.dataTransfer) return

  if (draggedColumnId.value === columnId) {
    e.dataTransfer.dropEffect = 'none'
    return
  }

  e.dataTransfer.dropEffect = 'move'

  const target = e.currentTarget as HTMLElement
  if (target) {
    target.style.backgroundColor = 'var(--accent)'
    target.querySelectorAll<HTMLElement>('*').forEach((el) => {
      el.style.color = 'var(--accent-foreground)'
    })
  }
}

const handleDragEnter = (e: DragEvent) => {
  if (draggedColumnId.value === columnId) {
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'none'
    }
    return
  }

  if (e.dataTransfer) {
    e.dataTransfer.dropEffect = 'move'
  }
}

const handleDragLeave = (e: DragEvent) => {
  const target = e.currentTarget as HTMLElement
  if (target) {
    target.style.backgroundColor = ''
    target.querySelectorAll<HTMLElement>('*').forEach((el) => {
      el.style.color = ''
    })
  }
}

const handleDrop = (e: DragEvent) => {
  if (!e.dataTransfer) {
    return
  }

  const dataTransferColumnId = e.dataTransfer.getData('text/plain')
  const actualDraggedColumnId = dataTransferColumnId || draggedColumnId.value

  if (actualDraggedColumnId && actualDraggedColumnId !== columnId) {
    emit('reorder', actualDraggedColumnId, columnId)
  }

  const target = e.currentTarget as HTMLElement
  if (target) {
    target.style.backgroundColor = ''
    target.querySelectorAll<HTMLElement>('*').forEach((el) => {
      el.style.color = ''
    })
  }
}

const sortIcon = computed(() => {
  if (!isSortable) return null
  if (sortDirection === 'asc') return ArrowUpIcon
  if (sortDirection === 'desc') return ArrowDownIcon
  return ArrowsUpDownIcon
})
</script>

<template>
  <!-- Resize strip stays outside `draggable` — browsers suppress click/dblclick on draggable trees. -->
  <div
    data-column-header-group
    class="group/header relative flex w-full min-w-0 items-stretch text-xs font-medium"
    @dragover.prevent.stop="handleDragOver"
    @dragenter.prevent.stop="handleDragEnter"
    @dragleave.prevent.stop="handleDragLeave"
    @drop.prevent.stop="handleDrop"
  >
    <div
      class="-mx-1 flex min-w-0 flex-1 cursor-grab items-center gap-2 bg-background/95 py-2 pl-1 pr-6 backdrop-blur supports-[backdrop-filter]:bg-background/95 transition-colors hover:bg-muted/50"
      :class="{
        'cursor-col-resize': isResizing,
        'cursor-grabbing': isDragging,
        'cursor-grab': !isDragging && !isResizing,
      }"
      :data-column-id="columnId"
      :data-pinned="isPinned"
      draggable="true"
      @dragstart="handleDragStart"
      @dragend="handleDragEnd"
    >
      <div class="flex min-w-0 flex-1 items-center">
        <!-- flex-1 on a plain wrapper so title width is correct regardless of Tooltip root DOM -->
        <div class="min-w-0 flex-1">
          <Tooltip>
            <TooltipTrigger as-child>
              <span
                data-column-header-title
                class="block min-w-0 w-full overflow-hidden text-clip whitespace-nowrap pl-1 pr-2 text-muted-foreground"
                :data-sort-direction="sortDirection ?? 'none'"
              >{{ name }}</span>
            </TooltipTrigger>
            <TooltipContent>{{ name }}</TooltipContent>
          </Tooltip>
        </div>
        <!-- Sort control flush right (before pr-6 resize strip); revealed on header group hover/focus-within -->
        <div
          v-if="isSortable"
          class="ml-auto flex shrink-0 items-center gap-0 transition-[gap] duration-150 pointer-coarse:gap-1 group-hover/header:gap-1 group-focus-within/header:gap-1"
        >
          <button
            data-sort-button
            type="button"
            class="header-sort-reveal flex h-5 shrink-0 items-center justify-center rounded-full text-muted-foreground/70 transition-[width,opacity] duration-150 hover:bg-primary/10 hover:text-primary"
            :class="sortDirection
              ? 'text-primary bg-primary/10 pointer-events-auto w-5 overflow-visible opacity-100'
              : [
                  'opacity-0 pointer-events-none w-0 overflow-hidden',
                  'max-md:pointer-events-auto max-md:w-5 max-md:overflow-visible max-md:opacity-100',
                  'pointer-coarse:pointer-events-auto pointer-coarse:w-5 pointer-coarse:overflow-visible pointer-coarse:opacity-100',
                  'group-hover/header:pointer-events-auto group-hover/header:w-5 group-hover/header:overflow-visible group-hover/header:opacity-100',
                  'group-focus-within/header:pointer-events-auto group-focus-within/header:w-5 group-focus-within/header:overflow-visible group-focus-within/header:opacity-100',
                ]"
            @click.stop="handleSort"
          >
            <component :is="sortIcon" class="h-4 w-4 shrink-0" />
          </button>
        </div>
      </div>
    </div>

    <!--
      Right-edge column separator + (optional) resize hit-area.
      The thin border is always rendered so every column has a visible
      divider — fixedWidth columns (Play, History) used to render `v-if`'d
      off and the separator went with them. Drag-to-resize / dblclick-to-
      autosize behaviours are still gated on `resizable`; the wrapper goes
      `pointer-events-none` for fixedWidth columns so it doesn't grab the
      cursor.
    -->
    <div
      data-resize-handle
      draggable="false"
      class="absolute right-0 top-0 bottom-0 z-20 w-2 select-none"
      :class="resizable ? 'cursor-col-resize' : 'pointer-events-none'"
      @mousedown.stop="resizable && handleResizeStart($event)"
      @dblclick.stop.prevent="resizable && handleResizeHandleDblClick($event)"
    >
      <div
        class="pointer-events-none absolute right-0 top-2 bottom-2 w-0.5 rounded-full bg-transparent"
        :class="[
          // Hidden until the header is hovered, as in Macro: no lines between columns.
          resizable ? 'transition-colors group-hover/header:bg-border' : '',
          { 'bg-primary w-1': isResizing },
        ]"
      />
    </div>
  </div>
</template>
